/**
 * Tinsmith brand glyph — the 90° HVAC duct elbow on a brand-blue rounded
 * square. Use this anywhere in-app you want to plant the Tinsmith mark:
 * headers, login hero, empty states, marketing splashes.
 *
 * The glyph path is the same one in `public/icons/icon-mono.svg` — single
 * filled shape, no seam crimps (those disappear at small sizes anyway).
 * Crimps live on the favicon / PWA icons because those render at app-icon
 * size where the detail reads.
 *
 * Sizing is controlled via the `size` prop:
 *   - `sm` (28px) — header / side rail
 *   - `md` (40px) — inline content blocks
 *   - `lg` (56px) — login hero, empty states
 *
 * Pass `nameAfter` to render the wordmark text next to the glyph in one
 * unit (e.g. for a sticky header).
 */

import { cn } from '../lib/cn.js';

export type BrandGlyphSize = 'sm' | 'md' | 'lg';

const SIZE_CLASS: Record<BrandGlyphSize, string> = {
  sm: 'h-7 w-7 rounded-md',
  md: 'h-10 w-10 rounded-lg',
  lg: 'h-14 w-14 rounded-lg',
};

const PATH_CLASS: Record<BrandGlyphSize, string> = {
  sm: 'h-[18px] w-[18px]',
  md: 'h-6 w-6',
  lg: 'h-9 w-9',
};

export function BrandGlyph({
  size = 'sm',
  className,
}: {
  size?: BrandGlyphSize;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex items-center justify-center',
        SIZE_CLASS[size],
        className,
      )}
      style={{ backgroundColor: 'var(--color-text-info)', color: 'var(--color-text-on-brand)' }}
    >
      <svg
        viewBox="0 0 512 512"
        className={PATH_CLASS[size]}
        aria-hidden
      >
        {/* 90° elbow path — identical to public/icons/icon-mono.svg so the
           in-app mark matches every favicon / PWA icon pixel-perfect. */}
        <path
          d="M 112 400 L 112 256 A 144 144 0 0 1 256 112 L 400 112 L 400 176 L 256 176 A 80 80 0 0 0 176 256 L 176 400 Z"
          fill="currentColor"
        />
      </svg>
    </span>
  );
}
